import { Component, type ReactNode } from "react";

/**
 * 렌더 중 예외를 잡아 **화면에 보여준다.**
 *
 * 이게 없던 탓에 로그인 뒤 React가 두 벌 로드되는 문제가 **완전한 흰 화면**으로만
 * 나타났다(원인 메시지는 콘솔에만 있었다). 사용자가 "흰 화면"이라고만 말할 수
 * 있는 상태는 고치는 데 몇 배가 든다.
 *
 * 프로덕션에서도 메시지를 보여준다. 여기 오는 건 우리 코드가 터진 것이고,
 * 서버 오류와 달리 비밀이 섞일 여지가 없다 — 대신 스택은 접어 둔다.
 */
type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("[render] 화면을 그리지 못했습니다", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-base font-semibold">화면을 표시하지 못했습니다</p>
        <p className="max-w-lg text-sm text-muted-foreground break-words">{error.message}</p>
        <div className="flex gap-2">
          <button className="rounded-md border px-4 py-2 text-sm" onClick={() => window.location.reload()}>
            새로고침
          </button>
          <button
            className="rounded-md border px-4 py-2 text-sm"
            onClick={() => this.setState({ error: null })}
          >
            다시 시도
          </button>
        </div>
        {error.stack && (
          <details className="max-w-lg text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">기술 정보</summary>
            <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-[11px] leading-snug">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
